'use client';

import type { DragEndEvent } from '@dnd-kit/core';
import { useId } from 'react';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { cn } from '@savvy-kit/lib/utils';
import { Tag } from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import { GripVertical } from 'lucide-react';

import type { ExportableColumn } from '../../types/export';
import { reorderColumnsWithinSubset } from '../../utils/column-ordering';

export type SortableColumnChipsProps = {
  readonly columns: readonly ExportableColumn[];
  readonly removable: boolean;
  readonly onRemove: (columnId: string) => void;
  /** Omit to render plain, non-draggable chips (no grip handle, no dnd-kit context). */
  readonly onReorder?: (nextColumnIds: string[]) => void;
};

// Chips wrap across multiple rows, so rectSortingStrategy (grid-aware) is used
// instead of the single-line horizontal/vertical strategies used elsewhere in
// this repo (offerings' SortableList, this package's table header reordering).
export function SortableColumnChips({ columns, removable, onRemove, onReorder }: SortableColumnChipsProps) {
  const ids = columns.map((column) => column.id);
  const instructionsId = useId();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (isNil(onReorder)) {
    return (
      <div className="flex flex-wrap gap-2 items-center max-h-[200px] overflow-y-auto">
        {columns.map((column) => (
          <Tag
            key={column.id}
            variant="filter"
            dismissible={removable}
            onDismiss={removable ? () => onRemove(column.id) : undefined}
          >
            {column.header}
          </Tag>
        ))}
      </div>
    );
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over === null || active.id === over.id) return;

    onReorder(
      reorderColumnsWithinSubset({
        columnOrder: ids,
        movableColumnIds: ids,
        activeId: String(active.id),
        overId: String(over.id),
      }),
    );
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <span id={instructionsId} className="sr-only">
        Press Space to pick up a column, arrow keys to reorder, Space to drop.
      </span>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="flex flex-wrap gap-2 items-center max-h-[200px] overflow-y-auto">
          {columns.map((column) => (
            <SortableChip
              key={column.id}
              column={column}
              removable={removable}
              onRemove={() => onRemove(column.id)}
              describedBy={instructionsId}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableChip({
  column,
  removable,
  onRemove,
  describedBy,
}: {
  readonly column: ExportableColumn;
  readonly removable: boolean;
  readonly onRemove: () => void;
  readonly describedBy: string;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    attributes: { roleDescription: 'sortable' },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-1">
      <button
        ref={setActivatorNodeRef}
        type="button"
        {...attributes}
        {...listeners}
        aria-describedby={describedBy}
        aria-label={`Drag to reorder ${column.header}`}
        className={cn(
          'cursor-grab rounded p-0.5 text-muted-foreground/60',
          'hover:text-muted-foreground active:cursor-grabbing',
          'focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1',
        )}
      >
        <GripVertical aria-hidden className="h-3.5 w-3.5" />
      </button>
      <Tag variant="filter" dismissible={removable} onDismiss={removable ? onRemove : undefined}>
        {column.header}
      </Tag>
    </div>
  );
}
